import * as React from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import Popper from "@mui/material/Popper";
import { useTheme, styled } from "@mui/material/styles";
import { List, useListRef } from "react-window";
import Typography from "@mui/material/Typography";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Box } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";

const LISTBOX_PADDING = 8; // px

function RowComponent({ index, itemData, style }) {
  const dataSet = itemData[index];
  const inlineStyle = {
    ...style,
    top: (style.top ?? 0) + LISTBOX_PADDING,
  };

  if ("group" in dataSet) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const { key, ...optionProps } = dataSet[0];
  const selected = optionProps["aria-selected"];
  const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
  return (
    <>
      <Typography key={key} {...optionProps} noWrap style={inlineStyle}>
        <SelectionIcon
          fontSize="small"
          style={{ marginRight: 8, padding: 9, boxSizing: "content-box" }}
        />
        {`${dataSet[1]}`}
      </Typography>
    </>
  );
}

// Adapter for react-window v2

RowComponent.propTypes = {
  index: PropTypes.number.isRequired,
  itemData: PropTypes.array.isRequired,
  style: PropTypes.object.isRequired,
};

const ListboxComponent = React.forwardRef(
  function ListboxComponent(props, ref) {
    const { children, internalListRef, onItemsBuilt, ...other } = props;
    const itemData = [];
    const optionIndexMap = React.useMemo(() => new Map(), []);

    children.forEach((item) => {
      itemData.push(item);
      if ("children" in item && Array.isArray(item.children)) {
        itemData.push(...item.children);
      }
    });

    // Map option values to their indices in the flattened array
    itemData.forEach((item, index) => {
      if (Array.isArray(item) && item[1]) {
        optionIndexMap.set(item[1], index);
      }
    });

    React.useEffect(() => {
      if (onItemsBuilt) {
        onItemsBuilt(optionIndexMap);
      }
    }, [onItemsBuilt, optionIndexMap]);

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
      noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child) => {
      if (child.hasOwnProperty("group")) {
        return 48;
      }
      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    // Separate className for List, other props for wrapper div (ARIA, handlers)
    const { className, style, ...otherProps } = other;

    return (
      <div ref={ref} {...otherProps}>
        <List
          className={className}
          listRef={internalListRef}
          key={itemCount}
          rowCount={itemCount}
          rowHeight={(index) => getChildSize(itemData[index])}
          rowComponent={RowComponent}
          rowProps={{ itemData }}
          style={{
            height: getHeight() + 2 * LISTBOX_PADDING,
            width: "100%",
          }}
          overscanCount={5}
          tagName="ul"
        />
      </div>
    );
  },
);

ListboxComponent.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  internalListRef: PropTypes.any,
  onItemsBuilt: PropTypes.func.isRequired,
  style: PropTypes.object,
};

function random(length) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = [];

  for (let i = 0; i < length; i += 1) {
    result.push(
      i + characters.charAt(Math.floor(Math.random() * characters.length)),
    );
  }

  return result;
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

export default function VirtualizedAutoCompleteField({
  label,
  options,
  value,
  onChange,
  disabled,
  limit = 3,
  onSearch,
  searching,
}) {
  // Use react-window v2's useListRef hook for imperative API access
  const internalListRef = useListRef(null);
  const optionIndexMapRef = React.useRef(new Map());
  const debounceRef = useRef();
  const [inputValue, setInputValue] = useState("");

  //const [currentValue, setcurrentValue] = useState([]);

  const optionMap = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(o.id, o));
    return map;
  }, [options]);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [options]);

  const currentValue = useMemo(() => {
    if (!value?.length) return [];
    return value.map((id) => optionMap.get(id)).filter(Boolean);
  }, [value, optionMap]);

  const handleItemsBuilt = React.useCallback((optionIndexMap) => {
    optionIndexMapRef.current = optionIndexMap;
  }, []);

  // Handle keyboard navigation by scrolling to highlighted option
  const handleHighlightChange = (event, option) => {
    if (option && internalListRef.current) {
      const index = optionIndexMapRef.current.get(option);
      if (index !== undefined) {
        internalListRef.current.scrollToRow({ index, align: "auto" });
      }
    }
  };

  const handleChange = (e, newValue) => {
    // Send only ID array to parent
    if (onChange) {
      onChange(newValue.map((v) => v.id));
    }
  };

  const handleInputChange = (event, value, reason) => {
    if (reason === "input") {
      setInputValue(value);

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearch?.(value);
      }, 300);
    }
  };

  return (
    <Autocomplete
      sx={{ width: "100%" }}
      multiple
      limitTags={limit}
      value={currentValue}
      filterOptions={(x) => x}
      options={sortedOptions}
      disabled={disabled}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      //groupBy={(option) => option[0].toUpperCase()}
      //groupBy={(option) => option.displayName?.[0]?.toUpperCase()}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !e.target.value) {
              e.stopPropagation();
            }
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {searching ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option, state) => [
        props,
        option.displayName,
        state.index,
      ]}
      getOptionLabel={(option) => option.displayName || ""}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderGroup={(params) => params}
      onHighlightChange={handleHighlightChange}
      onChange={handleChange}
      slots={{
        popper: StyledPopper,
      }}
      slotProps={{
        listbox: {
          component: ListboxComponent,
          internalListRef,
          onItemsBuilt: handleItemsBuilt,
          role: "listbox",
        },
      }}
    />
  );
}
